import Utils from './Utils';
import Ajax from './ajax';
import cache, {_cache} from './cache';
import {JUST_FOR_YOU_CATEGORY} from '../consts';

function event(path) {
  return Ajax.post('/api/event', {path: path});
}

function error(message) {
  return Ajax.post('/api/error', {message: message});
}

function getBrands(query, activeOnly) {
  let url = '/api/brand/search';
  let parameters = [];
  if (typeof(query) === 'string' && query.length > 0) {
    parameters.push('query=' + query);
  }

  if (activeOnly !== undefined) {
    parameters.push('active_only=' + (activeOnly ? 'true' : 'false'));
  }

  if (parameters.length > 0) {
    url += '?' + parameters.join('&');
  }

  return Ajax.get(url);
}

function getBrand(brandToken, cached) {
  if (cached && _cache.brands[brandToken]) {
    return Promise.resolve(_cache.brands[brandToken]);
  }

  let returnValue = Ajax.get(`/api/brand/${brandToken}`);

  returnValue.then((brand) => {
    cache.setBrands([brand]);
  });

  return returnValue;
}

function getClosestBrandRetailer(brandToken) {
  return Ajax.get(`/api/retailer/closest-brand-retailer/${brandToken}`);
}

function getAccountStatus(retailerToken) {
  return Ajax.get(`/api/retailer/${retailerToken}/account-status`);
}

function getPendingPaymentsStatus(retailerToken) {
  return Ajax.get(`/api/retailer/${retailerToken}/pending-payment`);
}

function getOutstandingIncomingPayments(retailerToken) {
  return Ajax.get(`/api/payment/outstanding-incoming/${retailerToken}`);
}

function saveBrand(brand) {
  let returnValue = Ajax.post('/api/brand', brand);

  returnValue.then((brand) => {
    cache.setBrands([brand]);
  });

  return returnValue;
}

function getBrandProducts(brandToken) {
  return Ajax.get(`/api/brand/${brandToken}/products`);
}

function brandLead(payload) {
  return Ajax.post('/api/brand/lead', payload);
}

/* --- Emails --- */

function searchEmails(request) {
  if (!request) request = {};
  return Ajax.post(`/api/email/search`, request);
}

function sendEmail(token) {
  return Ajax.post(`/api/email/send/${token}`);
}

/* --- Recommendation Emails --- */

function getPendingRecommendationEmails(request) {
  if (!request) {
    request = {template: "RECOMMENDATIONS"};
  }
  return Ajax.post(`/api/email-recommendations/pending`, request);
}

function updatePendingRecommendationEmails(emails) {
  return Ajax.post(`/api/email-recommendations/update-pending`, emails);
}

/* --- Categories --- */

function getCategories(cached) {

  function format(categories) {
    return categories
      .reduce((mapping, category) => {
        mapping[category.name] = (category.sub_categories || [])
          .map(subcategory => subcategory.name);

        return mapping;
      }, {});
  }

  if (cached && cache.getCategories() !== null) {
    return Promise.resolve(cache.getCategories());
  }

  return Ajax.get('/api/category/new')
    .then((response) => {
      let categoryMapping = format(response);
      cache.setCategories(categoryMapping);
      return Promise.resolve(categoryMapping);
    });
}

/* --- Retailer --- */

function getRetailer(retailerToken, cached) {
  // TODO: Throw Exception if no retailerToken is given
  cached = cached || false;
  if (cached && _cache.retailer && _cache.retailer.token === retailerToken) {
    return Promise.resolve(_cache.retailer);
  }

  let getPromise = Ajax.get(`/api/retailer/${retailerToken}`);

  getPromise.then(
    (retailer) => {
      cache.setRetailer(retailer);
      return retailer;
    },
    (errorData) => {
      cache.setRetailer(null);
      return errorData;
    }
  );

  return getPromise;
}

function getRetailers(query, activeOnly) {
  let url = '/api/retailer/search';
  let parameters = [];
  if (typeof(query) === 'string' && query.length > 0) {
    parameters.push('query=' + query);
  }

  if (activeOnly !== undefined) {
    parameters.push('active_only=' + (activeOnly ? 'true' : 'false'));
  }

  if (parameters.length > 0) {
    url += '?' + parameters.join('&');
  }

  return Ajax.get(url);
}

function createRetailer(retailer) {
  let postPromise = Ajax.post('/api/retailer', retailer);
  postPromise.then(
    (createdRetailer) => {
      cache.setRetailer(createdRetailer);
      return createdRetailer;
    },
    (errorData) => {
      cache.setRetailer(null);
      return errorData;
    }
  );

  return postPromise;
}

function refreshCurrentRecommendations() {
  let recommendations = cache.getRecommendations();
  if (recommendations && recommendations.url) {
    let url = recommendations.url;
    let promise = Ajax.get(recommendations.url);
    promise.then((response) => {
      cache.setRecommendations(url, response);
      cache.setProducts(response.products);
      cache.setBrands(response.brands);
    });
    return promise;
  } else {
    return Promise.resolve(recommendations);
  }
}

function retailerAddRecommendation(retailerToken, productToken) {
  return retailerAddRecommendations(retailerToken, [productToken]);
}

function retailerAddRecommendations(retailerToken, productTokens) {
  let url = `/api/retailer/${retailerToken}/add-recommendations`;
  let data = [];
  productTokens.forEach((token) => {
    data.push({"token": token});
  });

  return Ajax.post(url, data);
}

function retailerRemoveRecommendation(retailerToken, productToken) {
  return retailerRemoveRecommendations(retailerToken, [productToken]);
}

function retailerRemoveRecommendations(retailerToken, productTokens) {
  let url = `/api/retailer/${retailerToken}/remove-recommendations`;
  let data = [];
  productTokens.forEach((token) => {
    data.push({"token": token});
  });

  return Ajax.post(url, data);
}

function retailerAddCollectionRecommendation(retailerToken, collectionToken) {
  return retailerAddCollectionRecommendations(retailerToken, [collectionToken]);
}

function retailerAddCollectionRecommendations(retailerToken, collectionTokens) {
  let url = `/api/retailer/${retailerToken}/add-collection-recommendations`;
  let data = collectionTokens.map(token => {
    return {"token": token};
  });

  return Ajax.post(url, data);
}

function retailerRemoveCollectionRecommendation(retailerToken, collectionToken) {
  return retailerRemoveCollectionRecommendations(retailerToken, [collectionToken]);
}

function retailerRemoveCollectionRecommendations(retailerToken, collectionTokens) {
  let url = `/api/retailer/${retailerToken}/remove-collection-recommendations`;
  let data = collectionTokens.map(token => {
    return {"token": token};
  });

  return Ajax.post(url, data);
}

function retailerClearRecommendations(retailerToken) {
  return Ajax.post(`/api/retailer/${retailerToken}/clear-recommendations`);
}

function retailerAddFromRetailer(retailerToken, fromToken) {
  return Ajax.post(`/api/retailer/${retailerToken}/add-recommendations/${fromToken}`);
}

function retailerLead(retailer, payload) {
  return Ajax.post(`/api/retailer/${retailer}/lead`, payload);
}

/* --- Product --- */

function getProduct(productToken, cached) {
  // TODO: Throw Exception if no id is given

  if (cached && _cache.products[productToken]) {
    const product = _cache.products[productToken];
    const brand = _cache.brands[product.brand_token];
    if (product && brand) {
      return Promise.resolve({
        products: {[productToken]: product},
        brands: {[product.brand_token]: brand},
      });
    }
  }

  let returnValue = Ajax.get(`/api/product/${productToken}`);
  returnValue.then(response => {
    cache.setProducts(response.products);
    cache.setBrands(response.brands);
  });

  return returnValue;
}

/**
 * Retrieves a given product from cache, if exists or fetch from the API if needed.
 */
function getProductCache(productToken) {
  return getProduct(productToken, true);
}

function saveProduct(product) {
  let returnPromise = Ajax.post('/api/product', product);

  returnPromise.then((response) => {
    cache.setProducts(response.products);
  });

  return returnPromise;
}

function getProducts(query, activeOnly, retailerToken) {
  let url = '/api/product/search';
  let parameters = [];
  if (typeof(query) === 'string' && query.length > 0) {
    parameters.push('query=' + query);
  }

  if (activeOnly !== undefined) {
    parameters.push('active_only=' + (activeOnly ? 'true' : 'false'));
  }

  if (retailerToken !== undefined) {
    parameters.push('retailer_token=' + retailerToken);
  }

  if (parameters.length > 0) {
    url += '?' + parameters.join('&');
  }

  return Ajax.get(url)
    .then(
      recommendations => {
        cache.setProductSearchRecommendations(recommendations);
        return recommendations;
      }
    );
}

function saveProductOption(productToken, data) {
  let promise = Ajax.post(`/api/product/${productToken}/option`, data);

  promise.then(() => {
    // Remove product from cache
    cache.removeProduct(productToken);
    // Re-retrieve product
    return getProduct(productToken);
  });

  return promise;
}

function deleteProductOption(productToken, optionToken) {
  let promise = Ajax.delete(`/api/product/${productToken}/option/${optionToken}`);

  promise.then(() => {
    // Remove product from cache
    cache.removeProduct(productToken);
    // Re-retrieve product
    return getProduct(productToken);
  });

  return promise;
}

function createProductBrowseNewPayload(category, subcategory, filter) {
  let categories = [subcategory ? subcategory : category];
  let payload = {
    categories,
    maker_first: category === JUST_FOR_YOU_CATEGORY
  };

  if (filter) {
    payload[filter.value] = true;
  }

  return payload;
}

function generateProductBrowseKey(payload) {
  let values = Object.keys(payload).filter(key => payload[key] === true).sort();
  let cacheKey = (payload.categories || []).join(':');
  if (values.length > 0) cacheKey += '+' + values.join(':');
  if (payload.query) cacheKey += '+query:' + payload.query;

  return cacheKey;
}

function productBrowseNew(payload, skipCache) {
  const cacheKey = generateProductBrowseKey(payload);
  const currentData = cache.getCategoryData(cacheKey);

  if (!skipCache && !payload.pagination_token && currentData) {
    return Promise.resolve(currentData);
  }

  const appendResultsToRecommendations = newData => {
    if (!currentData) return newData;

    let returnValue = Utils.clone(currentData, true);
    returnValue.display_order = returnValue.display_order.concat(newData.display_order);
    returnValue.products = Object.assign(returnValue.products, newData.products);
    returnValue.brands = Object.assign(returnValue.brands || {}, newData.brands || {});
    returnValue.collections = Object.assign(returnValue.collections || {}, newData.collections || {});

    if (newData.next_request) {
      returnValue.next_request = newData.next_request;
    } else {
      delete returnValue.next_request;
    }

    return returnValue;
  };

  return Ajax.post(`/api/product/search-new`, payload)
    .then((response) => {
      let newContent = response;

      // TODO need better logic to determine when to append results
      if (!skipCache) {
        newContent = appendResultsToRecommendations(response);
        newContent.cacheKey = cacheKey;
      }
      cache.setCategoryData(cacheKey, newContent);
      return newContent;
    });
}

function activateProducts(tokens) {
  return Ajax.post('/api/product/activate', tokens);
}

function deactivateProducts(tokens) {
  return Ajax.post('/api/product/deactivate', tokens);
}

/**
 * This gets the value from the local cache only.
 */
function getProductOption(productToken, optionToken) {
  return new Promise((accept, reject) => {
    getProductCache(productToken).then(
      () => {
        // cache is populated by getProduct / getProductCache
        let i, product = _cache.products[productToken];

        for (i = 0; i < product.options.length; i++) {
          if (product.options[i].token === optionToken) {
            accept(product.options[i]);
            return;
          }
        }

        reject('Invalid option token for the given product.');
      },
      reject
    );
  });
}

/* --- Cart --- */

function getCart(cached) {
  if (cached && _cache.cart) {
    return Promise.resolve(_cache.cart);
  }

  let returnValue = Ajax.get('/api/cart');
  returnValue.then((response) => {
    cache.setCart(response);
  });

  return returnValue;
}

// TODO: eventually remove this.
function getCartCache() {
  return getCart(true);
}

function addToCart(data) {
  return Ajax.post('/api/cart/add-items', data)
    .then(response => {
      cache.setLastAddedProductToken(data[0].product_token);
      cache.setCart(response);
      return response;
    });
}

function removeFromCart(itemToken) {
  let itemList = itemToken;
  if (!Array.isArray(itemList)) itemList = [itemList];
  let returnValue = Ajax.post('/api/cart/remove-items', itemList);
  returnValue.then((response) => {
    cache.setCart(response);
  });

  return returnValue;
}

function submitOrder(addressToken, instrumentToken, promoCode) {
  let payload = {};
  if (addressToken) payload.address_token = addressToken;
  if (instrumentToken) payload.instrument_token = instrumentToken;
  if (promoCode) payload.promo_codes = [promoCode];
  return Ajax.post('/api/order', payload);
}

function register(data, requestUpdates) {
  let url = '/api/user';

  let parameters = [];
  if (requestUpdates !== undefined) {
    parameters.push('request-updates=' + (requestUpdates ? 'true' : 'false'));
  }
  if (parameters.length > 0) {
    url += '?' + parameters.join('&');
  }

  return Ajax.post(url, data);
}

/* --- User --- */
function userLogin(alias, code) {
  let data = {};
  if (alias.indexOf('@') >= 0) {
    data.email_address = alias;
  } else {
    data.phone_number = alias;
  }

  if (code) data.code = code;

  let postPromise = Ajax.post('/api/user/login', data);

  if (code) {
    postPromise.then(
      (user) => {
        cache.setUser(user);
        return user;
      }
    );
  }
  return postPromise;
}

function userLogout() {
  return Ajax.post('/api/user/logout')
    .then(
      (response) => {
        cache.setUser(null);
        return (response);
      }
    );
}

function createUser(data) {
  let promise = Ajax.post('/api/user', data);

  promise.then((createdUser) => {
    cache.setUser(createdUser);
    return createdUser;
  });

  return promise;
}

function impersonateUserByToken(userToken) {
  let promise = Ajax.post('/api/user/impersonate?token=' + userToken);

  promise.then((impersonatedUser) => {
    cache.setUser(impersonatedUser);
    return impersonatedUser;
  });

  return promise;
}

function importBrand(brandUrl, brandName) {
  return Ajax.post('/api/import/brand?url=' + brandUrl + '&name=' + brandName);
}

function backfill(name, dryRun, minId, maxId, windowSize) {
  let data = {name: name, dry_run: dryRun, min_id: minId, max_id: maxId, window_size: windowSize};
  return Ajax.post('/api/backfill/start', data);
}

function getBackfillers() {
  return Ajax.get('/api/backfill');
}

function unimpersonateUser() {
  let promise = Ajax.post('/api/user/unimpersonate');

  promise.then((adminUser) => {
    cache.setUser(adminUser);
    return adminUser;
  });

  return promise;
}

function getLastSelectedRetailer() {
  let lastSelectedRetailerToken = cache.getLastSelectedRetailerToken();
  if (lastSelectedRetailerToken) return Promise.resolve(lastSelectedRetailerToken);

  let promise = Ajax.get('/api/user/last-selected-retailer');

  promise.then((lastSelectedRetailerToken) => {
    cache.setLastSelectedRetailerToken(lastSelectedRetailerToken);
    return lastSelectedRetailerToken;
  });

  return promise;
}

function resetLastSelectedRetailer() {
  // Reset the cache right away. No need to wait for the network!
  cache.setLastSelectedRetailerToken(null);

  return Ajax.post('/api/user/reset-last-selected-retailer');
}

function getUser(cached) {
  if (getUser.promise) return getUser.promise;

  cached = cached || false;

  if (cached && _cache.user !== null) {
    return Promise.resolve(_cache.user);
  }

  getUser.promise = Ajax.get('/api/user')
    .then((response) => {
      cache.setUser(response);
      getUser.promise = null;
      return response;
    });

  return getUser.promise;
}

function saveUserAddress(address) {
  let returnValue = Ajax.post('/api/user/addresses', address);

  returnValue.then((response) => {
    let user = Utils.clone(_cache.user);
    user.addresses = response;
    cache.setUser(user);
  });

  return returnValue;
}

/* --- Instrument --- */

function plaidTokenViaStripe(data) {
  return Ajax.post('/api/instrument/plaid-token-via-stripe', data);
}

function stripeLinkCard(data) {
  return Ajax.post('/api/instrument/stripe-link-card', data);
}

function setDefaultInstrument(instrumentToken) {
  return Ajax.post('/api/instrument/set-default', {token: instrumentToken});
}

function removeInstrument(instrumentToken) {
  return Ajax.post('/api/instrument/unlink', {token: instrumentToken});
}

/* --- Payment --- */

function chargeRetailer(request) {
  return Ajax.post('/api/payment/add-retailer-payment', request);
}

/* --- Orders --- */

function getOrder(orderToken) {
  return Ajax.get(`/api/order/${orderToken}`);
}

function getOrders(limit, paginationToken) {
  let queryParams = [];
  if (limit) queryParams.push("limit=" + limit);
  if (paginationToken) queryParams.push("pagination-token=" + paginationToken);

  let url = '/api/order';
  if (queryParams.length > 0) url += '?' + queryParams.join('&');

  return Ajax.get(url);
}

function cancelOrder(orderToken) {
  return Ajax.post(`/api/order/${orderToken}/cancel`);
}

function updateOrderItem(orderToken, orderItem) {
  return Ajax.post(`/api/order/${orderToken}/item`, orderItem);
}

function cancelOrderItems(orderToken, orderItemTokens) {
  return Ajax.post(`/api/order/${orderToken}/cancel-items`, orderItemTokens);
}

function removeOrderItems(orderToken, orderItemTokens) {
  return Ajax.post(`/api/order/${orderToken}/remove-items`, orderItemTokens);
}

function updateBrandOrder(orderToken, brandOrder) {
  return Ajax.post(`/api/order/${orderToken}/brand-order`, brandOrder);
}

function createShipment(orderToken, orderItemTokens) {
  let data = {
    order_token: orderToken,
    order_item_tokens: orderItemTokens
  };

  return Ajax.post(`/api/order/${orderToken}/shipment`, data);
}

function updateShipment(payload) {
  return Ajax.post(`/api/order/${payload.order_token}/shipment`, payload);
}

function removeShipment(orderToken, shipmentToken) {
  return Ajax.post(`/api/order/${orderToken}/remove-shipment/${shipmentToken}`);
}

/* --- Product Collections --- */

function getProductCollection(collectionToken) {
  return Ajax.get(`/api/product-collection/${collectionToken}`);
}

let getProductCollectionsPromise = null;
function getProductCollections(cached) {
  if (cached && cache.getProductCollections() !== null) {
    return Promise.resolve(cache.getProductCollections());
  }

  if (getProductCollectionsPromise) return getProductCollectionsPromise;

  getProductCollectionsPromise = Ajax.get('/api/product-collection');
  getProductCollectionsPromise.then(
    response => {
      getProductCollectionsPromise = null;
      cache.setProductCollections(response);
      return response;
    }
  );

  return getProductCollectionsPromise;
}

function createOrUpdateProductCollection(payload) {
  return Ajax.post('/api/product-collection', payload);
}

/* Admin helpers */

function retailerFromLead(leadLine) {
  return Ajax.post('/api/retailer/retailer-from-lead', {lead_csv: leadLine});
}

/* --- Listeners --- */

/**
 * Sets the state for the given key when the eventType associated cached value changes.
 * This will also trigger the function `set{stateKey}Value` if it exists within the component object.
 * @param component -- object: the component for this to work on. usually the `this` value.
 * @param stateKey -- string: the state key value.
 * @param eventType -- enum string: from the cache types.
 */
function startListen(component, stateKey, eventType) {
  let eventKeyFunction = '__x_' + cache.eventTypes[eventType];
  if (component[eventKeyFunction]) {
    console.error("statListen() -- trying to register same event type twice.");
    return;
  }

  component[eventKeyFunction] = function () {
    let payload = {};
    let setValueFunctionName = 'set' + stateKey + 'Value';

    payload[stateKey] = cache.getForEventType(eventType);
    component.setState(payload);

    if (component[setValueFunctionName] && typeof(component[setValueFunctionName]) === 'function') {
      component[setValueFunctionName](payload[stateKey]);
    }
  };

  component[eventKeyFunction]();

  cache.addChangeListener(component[eventKeyFunction], eventType);
}

function stopListen(component, eventType) {
  let eventKeyFunction = '__x_' + cache.eventTypes[eventType];
  cache.removeChangeListener(component[eventKeyFunction], eventType);

  component[eventKeyFunction] = null;
}

function userStartListen(component, stateKey) {
  startListen(component, stateKey, cache.eventTypes.USER_CHANGED);
}

function userStopListen(component) {
  stopListen(component, cache.eventTypes.USER_CHANGED);
}

function orderListStartListen(component, stateKey) {
  startListen(component, stateKey, cache.eventTypes.ORDER_LIST_CHANGED);
}

function orderListStopListen(component, stateKey) {
  stopListen(component, stateKey, cache.eventTypes.ORDER_LIST_CHANGED);
}

function internalRecommendations() {
  return Ajax.get(`/api/internal/recommendations/`);
}

function validatePromoCodes(promoCodes) {
  let data = {
    'promo_codes': promoCodes
  };
  return Ajax.post('/api/cart/validate-promo-codes', data);
}

export default {
  Utils,
  cache,
  event,
  error,

  getCategories,

  /* Brand */
  getBrands,
  getBrand,
  saveBrand,
  getBrandProducts,
  brandLead,

  /* Emails */
  searchEmails,
  sendEmail,

  /* Recommendation Emails */
  getPendingRecommendationEmails,
  updatePendingRecommendationEmails,

  /* Retailer */
  getRetailer,
  getRetailers,
  createRetailer,
  refreshCurrentRecommendations,
  retailerAddRecommendation,
  retailerAddRecommendations,
  retailerRemoveRecommendation,
  retailerRemoveRecommendations,
  retailerAddCollectionRecommendation,
  retailerAddCollectionRecommendations,
  retailerRemoveCollectionRecommendation,
  retailerRemoveCollectionRecommendations,
  retailerAddFromRetailer,
  retailerClearRecommendations,
  retailerLead,
  getClosestBrandRetailer,
  getAccountStatus,
  getPendingPaymentsStatus,

  /* Product */
  getProduct,
  getProductCache,
  getProductOption,
  deleteProductOption,
  saveProduct,
  getProducts,
  saveProductOption,
  createProductBrowseNewPayload,
  generateProductBrowseKey,
  productBrowseNew,
  activateProducts,
  deactivateProducts,

  /* Product Collections */
  getProductCollection,
  getProductCollections,
  createOrUpdateProductCollection,

  /* Cart */
  getCart,
  getCartCache,
  addToCart,
  removeFromCart,
  submitOrder,
  register,

  /* User */
  userLogin,
  userLogout,
  getUser,
  createUser,
  saveUserAddress,
  impersonateUserByToken,
  importBrand,
  unimpersonateUser,
  getLastSelectedRetailer,
  resetLastSelectedRetailer,

  /* Instrument */
  plaidTokenViaStripe,
  stripeLinkCard,
  setDefaultInstrument,
  removeInstrument,

  /* Payment */
  chargeRetailer,
  getOutstandingIncomingPayments,

  /* Orders */
  getOrder,
  getOrders,
  cancelOrder,
  updateOrderItem,
  cancelOrderItems,
  removeOrderItems,
  updateBrandOrder,
  createShipment,
  updateShipment,
  removeShipment,

  /* Promo Codes */
  validatePromoCodes,

  /* Listener helpers */
  startListen,
  stopListen,
  userStartListen,
  userStopListen,
  orderListStartListen,
  orderListStopListen,

  /* More stuff */
  retailerFromLead,
  internalRecommendations,
  backfill,
  getBackfillers
}

export {Utils};



/** WEBPACK FOOTER **
 ** ./src/Api/index.js
 **/